import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { Goal, Team } from '../../types';
import { goalService } from '../../services/goalService';
import { useAuth } from '../../context/AuthContext';

interface CreateGoalDialogProps {
  open: boolean;
  onClose: () => void;
  onGoalCreated: () => void;
  teams: Team[];
}

const CreateGoalDialog: React.FC<CreateGoalDialogProps> = ({ 
  open, 
  onClose, 
  onGoalCreated, 
  teams 
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Form state
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [goalType, setGoalType] = useState('distance');
  const [targetValue, setTargetValue] = useState('');
  const [customUnit, setCustomUnit] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isCustomGoal, setIsCustomGoal] = useState(false);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      setSelectedTeamId('');
      setTitle('');
      setDescription('');
      setGoalType('distance');
      setTargetValue('');
      setCustomUnit('');
      setStartDate('');
      setEndDate('');
      setIsCustomGoal(false);
      setError('');
    }
  }, [open]);

  // Set default dates when dialog opens
  useEffect(() => {
    if (open) {
      const today = new Date();
      const nextMonth = new Date(today);
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      
      setStartDate(today.toISOString().split('T')[0]);
      setEndDate(nextMonth.toISOString().split('T')[0]);
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      setError('You must be logged in to create goals');
      return;
    }

    if (!selectedTeamId) {
      setError('Please select a team');
      return;
    }

    if (!title.trim()) {
      setError('Please enter a goal title');
      return;
    }

    if (!targetValue || isNaN(parseFloat(targetValue)) || parseFloat(targetValue) <= 0) {
      setError('Please enter a valid target value');
      return;
    }

    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (start >= end) {
      setError('End date must be after start date');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const goalData: Omit<Goal, 'id' | 'createdAt' | 'updatedAt' | 'currentValue' | 'isCompleted'> = {
        athleteId: user.id,
        teamId: selectedTeamId,
        title: title.trim(),
        description: description.trim() || undefined,
        goalType: goalType as Goal['goalType'],
        targetValue: parseFloat(targetValue),
        unit: isCustomGoal ? customUnit.trim() : goalService.getGoalTypeOptions().find(opt => opt.value === goalType)?.unit || 'units',
        startDate: { toDate: () => start } as any,
        endDate: { toDate: () => end } as any,
      };

      await goalService.createGoal(goalData);
      onGoalCreated();
    } catch (err: any) {
      setError(err.message || 'Failed to create goal');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const goalTypeOptions = goalService.getGoalTypeOptions();
  const selectedGoalType = goalTypeOptions.find(opt => opt.value === goalType);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddIcon />
            Create New Goal
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid size={12}>
              <FormControl fullWidth required>
                <InputLabel>Team</InputLabel>
                <Select
                  value={selectedTeamId}
                  onChange={(e) => setSelectedTeamId(e.target.value)}
                  disabled={loading}
                >
                  {teams.map((team) => (
                    <MenuItem key={team.id} value={team.id}>
                      {team.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={12}>
              <TextField
                fullWidth
                label="Goal Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                disabled={loading}
                placeholder="e.g., Run 50 miles this month"
              />
            </Grid>

            <Grid size={12}>
              <TextField
                fullWidth
                label="Description (optional)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={loading}
                multiline
                rows={2}
                placeholder="Describe your goal and motivation..."
              />
            </Grid>

            <Grid size={12}>
              <FormControl fullWidth required>
                <InputLabel>Goal Type</InputLabel>
                <Select
                  value={goalType}
                  onChange={(e) => {
                    setGoalType(e.target.value);
                    setIsCustomGoal(e.target.value === 'custom');
                  }}
                  disabled={loading}
                >
                  {goalTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Box>
                        <Typography variant="body1">{option.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 8 }}>
              <TextField
                fullWidth
                label="Target Value"
                type="number"
                value={targetValue}
                onChange={(e) => setTargetValue(e.target.value)}
                required
                disabled={loading}
                inputProps={{ min: 0, step: 0.1 }}
                placeholder={selectedGoalType?.unit ? `Enter target in ${selectedGoalType.unit}` : 'Enter target value'}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                label="Unit"
                value={isCustomGoal ? customUnit : (selectedGoalType?.unit || '')}
                onChange={(e) => setCustomUnit(e.target.value)}
                disabled={loading || !isCustomGoal}
                placeholder="e.g., miles, minutes, days"
                helperText={isCustomGoal ? 'Enter custom unit' : 'Auto-filled based on goal type'}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                required
                disabled={loading}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                required
                disabled={loading}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {selectedGoalType && (
              <Grid size={12}>
                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    <strong>{selectedGoalType.label}:</strong> {selectedGoalType.description}
                    {selectedGoalType.unit && (
                      <span> Target will be measured in {selectedGoalType.unit}.</span>
                    )}
                  </Typography>
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={loading || !selectedTeamId || !title.trim() || !targetValue || !startDate || !endDate}
          >
            {loading ? <CircularProgress size={20} /> : 'Create Goal'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateGoalDialog;
